import { Injectable, inject } from '@angular/core';
import { Observable, defer, from, map, switchMap } from 'rxjs';
import type { Schema } from 'firebase/ai';
import { ClinicalReportCapabilities } from '../../domain/models/clinical-report-capabilities.model';
import { ClinicalReportsRemoteDatasource } from '../datasources/remote/clinical-reports.remote-datasource';
import { DEFAULT_CLINICAL_REPORT_AI_MODEL } from '../../domain/models/clinical-report-ai-model.model';
import { ClinicalReportPromptVersion } from '../../domain/models/clinical-report-prompt-version.model';
import { ClinicalReportInconsistencia } from '../../domain/models/clinical-report.model';
import { ClinicalTextTemplate } from '../../domain/models/clinical-text-template.model';
import {
  ClinicalReportDebugInfo,
  ClinicalReportPreview,
  ClinicalReportPromptDraft,
  ClinicalReportRequest,
  ClinicalReportResult,
  IClinicalReportRepository,
} from '../../domain/repositories/clinical-report.repository';
import { buildClinicalReportSchema } from '../schemas/clinical-report.schema';
import { appendContextOnlyReportInstruction } from '../../domain/utils/context-only-report-prompt.util';
import { buildClinicalTemplateSchema } from '../schemas/clinical-template.schema';
import {
  CLINICAL_REPORT_SYSTEM_INSTRUCTION,
  CLINICAL_REPORT_USER_PROMPT,
} from '../../domain/constants/clinical-report-prompt.const';
import {
  CLINICAL_TEMPLATE_SYSTEM_INSTRUCTION,
  CLINICAL_TEMPLATE_USER_PROMPT,
} from '../../domain/constants/clinical-template-prompt.const';
import {
  PromptTemplateValues,
  buildPromptTemplateValues,
  interpolatePromptTemplate,
} from '../../domain/utils/prompt-template.util';
import { verifyFilledTemplate } from '../../domain/utils/template-placeholder.util';
import { ClinicalReportDto } from '../dtos/clinical-report.dto';
import { ClinicalTemplateResultDto } from '../dtos/clinical-template-result.dto';
import { ClinicalReportMapper } from '../mappers/clinical-report.mapper';

@Injectable()
export class ClinicalReportRepositoryImpl implements IClinicalReportRepository {
  private readonly datasource = inject(ClinicalReportsRemoteDatasource);
  private readonly mapper = inject(ClinicalReportMapper);

  public getCapabilities(): Observable<ClinicalReportCapabilities> {
    return this.datasource.getCapabilities();
  }

  public generate(request: ClinicalReportRequest): Observable<ClinicalReportResult> {
    const { template } = request;
    return template === null
      ? this.generateStructuredReport(request)
      : this.generateFromTemplate(request, template);
  }

  // Goes through the very same builders `send` uses, so the prompt lab can only ever show the
  // payload that a generation would really send.
  public async previewPayload(request: ClinicalReportRequest): Promise<ClinicalReportPreview> {
    const schema = await this.buildSchema(request);
    return { draft: this.prepareDraft(request), schemaJson: JSON.stringify(schema, null, 2) };
  }

  // `promptOverride` replaces the model, the temperature and both prompts -- but NOT the response
  // schema nor the parser this result is read with: those still follow from `template`, so editing
  // the free-text system instruction cannot make a run come back in the template shape.
  private send<T>(
    request: ClinicalReportRequest,
  ): Observable<{ dto: T; debug: ClinicalReportDebugInfo | null }> {
    const draft = this.prepareDraft(request);
    return from(this.buildSchema(request)).pipe(
      switchMap((schema) => this.datasource.generate({
        model: draft.model,
        systemInstruction: draft.systemInstruction,
        prompt: draft.prompt,
        ...(draft.temperature === null ? {} : { temperature: draft.temperature }),
        responseSchema: JSON.parse(JSON.stringify(schema)) as Record<string, unknown>,
      })),
      map(({ raw }) => ({
        dto: JSON.parse(raw) as T,
        debug: request.captureDebug === true ? { draft, rawResponse: raw } : null,
      })),
    );
  }

  private buildDraft(request: ClinicalReportRequest): ClinicalReportPromptDraft {
    const version = request.promptVersion ?? null;
    return version === null
      ? this.buildShippedDraft(request)
      : this.buildVersionedDraft(request, version);
  }

  private prepareDraft(request: ClinicalReportRequest): ClinicalReportPromptDraft {
    const draft = request.promptOverride ?? this.buildDraft(request);
    if (request.freeText.trim().length > 0) return draft;

    return {
      ...draft,
      prompt: appendContextOnlyReportInstruction(draft.prompt, {
        consultation: request.consultation,
        interfaceLanguage: request.interfaceLanguage ?? 'es',
        hasTemplate: request.template !== null,
      }),
    };
  }

  // The pre-catalogue behaviour, kept as the mandatory fallback: with the versions endpoint down,
  // the catalogue empty, or simply not loaded yet, a generation still goes out with a full prompt.
  private buildShippedDraft(request: ClinicalReportRequest): ClinicalReportPromptDraft {
    const { template } = request;
    return {
      model: request.model ?? DEFAULT_CLINICAL_REPORT_AI_MODEL,
      temperature: null,
      systemInstruction:
        template === null
          ? CLINICAL_REPORT_SYSTEM_INSTRUCTION
          : CLINICAL_TEMPLATE_SYSTEM_INSTRUCTION,
      prompt: interpolatePromptTemplate(
        template === null ? CLINICAL_REPORT_USER_PROMPT : CLINICAL_TEMPLATE_USER_PROMPT,
        this.buildPromptValues(request, template),
      ),
    };
  }

  private buildVersionedDraft(
    request: ClinicalReportRequest,
    version: ClinicalReportPromptVersion,
  ): ClinicalReportPromptDraft {
    const { template } = request;
    const versionModel =
      version.model.length > 0 ? version.model : DEFAULT_CLINICAL_REPORT_AI_MODEL;
    return {
      model: request.model ?? versionModel,
      temperature: version.temperature,
      systemInstruction:
        template === null ? version.systemPromptFree : version.systemPromptTemplate,
      prompt: interpolatePromptTemplate(
        template === null ? version.userPromptFree : version.userPromptTemplate,
        this.buildPromptValues(request, template),
      ),
    };
  }

  private buildPromptValues(
    request: ClinicalReportRequest,
    template: ClinicalTextTemplate | null,
  ): PromptTemplateValues {
    return buildPromptTemplateValues({
      freeText: request.freeText,
      interfaceLanguage: request.interfaceLanguage ?? 'es',
      consultation: request.consultation,
      templateText: template?.text ?? null,
    });
  }

  private buildSchema(request: ClinicalReportRequest): Promise<Schema> {
    return request.template === null ? buildClinicalReportSchema() : buildClinicalTemplateSchema();
  }

  private generateStructuredReport(
    request: ClinicalReportRequest,
  ): Observable<ClinicalReportResult> {
    return defer(() => this.send<ClinicalReportDto>(request)).pipe(
      map(({ dto, debug }) => ({
        kind: 'structured' as const,
        report: this.mapper.toClinicalReport(dto),
        debug,
      })),
    );
  }

  private generateFromTemplate(
    request: ClinicalReportRequest,
    template: ClinicalTextTemplate,
  ): Observable<ClinicalReportResult> {
    return defer(() => this.send<ClinicalTemplateResultDto>(request)).pipe(
      map(({ dto, debug }) => this.toTemplateResult(template.text, dto, debug)),
    );
  }

  private toTemplateResult(
    templateText: string,
    dto: ClinicalTemplateResultDto,
    debug: ClinicalReportDebugInfo | null,
  ): ClinicalReportResult {
    const text = dto.texto ?? '';
    const inconsistencias: readonly ClinicalReportInconsistencia[] = (
      dto.inconsistencias ?? []
    ).map((item) => ({
      descripcion: item.descripcion,
      fragmentos: item.fragmentos ?? [],
    }));

    return {
      kind: 'template',
      text,
      datosNoEncontrados: dto.datosNoEncontrados ?? [],
      inconsistencias,
      warnings: verifyFilledTemplate(templateText, text),
      debug,
    };
  }
}
